<template>
  <img
    ref="imageRef"
    v-bind="$attrs"
    :src="`${srcUrl}?width=768&optimizer=image`"
    :alt="alt"
    :loading="loading"
    :sizes="sizesAttribute"
    :srcset="srcSet"
  />
</template>
<!-- Using the Bunny AppImage Component as base since betterPortrait uses this CDN to serve images. -->

<!-- eslint-disable-next-line vue/block-lang -->
<script setup lang="ts">
import md5 from 'md5'

const props = withDefaults(defineProps<{
  email: string | null
  preset?: string
  alt?: string | null
  width?: number | null
  height?: number | null
  loading?: 'lazy' | 'eager'

}>(), {
  preset: 'default',
  alt: null,
  width: null,
  height: null,
  loading: 'lazy',
})

const imageRef = ref<HTMLImageElement | null>(null)
const imageWidth = ref<number | null>(null)
const imageWidthToWindowWidthRatio = ref<number | null>(null)
const runtimeConfig = useRuntimeConfig()
const baseUrl = runtimeConfig.public.BETTER_PORTRAIT_URL
const companyId = runtimeConfig.public.BETTER_PORTRAIT_COMPANY_ID

if (!baseUrl) {
  console.warn(
    'BETTER_PORTRAIT_URL is not set in the runtime config. Please set it to use BetterPortraitImage component.',
  )
}

if (!companyId) {
  console.warn(
    'BETTER_PORTRAIT_COMPANY_ID is not set in the runtime config. Please set it to use BetterPortraitImage component.',
  )
}

const screens = {
  'xs': '320px',
  'sm': '640px',
  'md': '768px',
  'lg': '1024px',
  'xl': '1280px',
  '2xl': '1600px',
  '3xl': '2000px',
  '4xl': '2560px',
  '5xl': '3840px',
}

// COMPUTED
const key = computed(() => {
  // md5 hash of email and companyId
  if (!props.email || !companyId) return null
  return md5(`${companyId}${props.email}`)
})

const sizesAttribute = computed(() => {
  if (imageWidthToWindowWidthRatio.value === null) {
    return '1px'
  }
  const imageWidthPercentage = Math.round(
    imageWidthToWindowWidthRatio.value * 100,
  )
  return `${imageWidthPercentage}vw`
})

const srcSet = computed(() => {
  return Object.entries(screens)
    .map(([key, screenWidth]) => {
      const width = parseInt(screenWidth.replace('px', ''))
      const path = getBunnySrcUrlWithParams(width, 'auto')
      return `${path} ${width}w`
    })
    .join(', ')
})

const srcUrl = computed(() => {
  // https:// delivery.betterportrait.ch/{companyId}/team/{key}/{preset}.png?{query}
  return `${baseUrl}/${companyId}/team/${key.value}/${props.preset}.png`
})

// FUNCTIONS
function getBunnySrcUrlWithParams(width: number, height: number | null | 'auto') {
  return `${srcUrl.value}?width=${width}&height=${height}&optimizer=image`
}

const updateImageWidth = useDebounceFn(() => {
  if (imageRef.value) {
    imageWidth.value = imageRef.value.clientWidth
    imageWidthToWindowWidthRatio.value = imageWidth.value / window.innerWidth
  }
}, 1000)

onMounted(async () => {
  updateImageWidth()
  window.addEventListener('resize', updateImageWidth)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateImageWidth)
})
</script>
